/**
 * mergeSuggestions — the accretive chip list (owner directive 2026-09-02).
 *
 * Chips arrive in two stages: a FAST set generated in parallel with the answer
 * (so the row is populated the moment it appears) and an UPGRADED set
 * generated with the delivered answer in context, landing seconds later.
 *
 * The owner's rule is that the second stage must never feel like the product
 * changing its mind:
 *
 *   "the upgraded suggested messages are added to the list of the fast items
 *    (prewarmed), the upgraded ones are brought to the beginning (first
 *    offers) and the other fast (less quality) ones are brought to the back."
 *
 * So this is accretion and re-ranking, never replacement. A chip the user can
 * see is never retracted out from under a click; better offers simply take the
 * lead and weaker ones fall back until they age off the end of the list.
 */

import { significantTokens } from '../threadMetadata/composeGreeting';

export type SuggestionStage = 'fast' | 'upgraded';

export interface MergedSuggestion {
  text: string;
  stage: SuggestionStage;
}

/** Visible ceiling. Two stages of 4 would otherwise stack to 8. */
export const MAX_MERGED_SUGGESTIONS = 5;

const normalize = (s: string): string => (s || '').replace(/\s+/g, ' ').trim().toLowerCase();

/**
 * Near-duplicate test for two CHIPS (the sibling of suggestionOverlapsThread,
 * which compares a chip against a thread). The two stages routinely phrase the
 * same idea differently — "Save this draft to Drive" vs "Save the consent
 * agreement draft to Google Drive" — and showing both would waste a slot and
 * read as a bug. Containment or a strong token majority counts as the same
 * offer; a couple of shared common words does not.
 */
export function chipsAreNearDuplicates(a: string, b: string): boolean {
  const na = normalize(a);
  const nb = normalize(b);
  if (!na || !nb) return false;
  if (na === nb) return true;
  if (na.includes(nb) || nb.includes(na)) return true;
  const ta = significantTokens(a);
  const tb = significantTokens(b);
  if (ta.size === 0 || tb.size === 0) return false;
  // The LEADING verb decides the offer: "draft the motion to compel" and
  // "review the motion to compel" share most of their words but ask for
  // opposite work, so a token ratio alone wrongly collapses them. Same opening
  // verb + a strong token majority means the same offer worded twice.
  //
  // Read the verb from the RAW text, not from significantTokens: that filter
  // drops everything under four characters, so every short verb — ask, get,
  // add, buy, cut, fix, log, pay, put, run, set, use — vanished and the check
  // silently compared the following NOUN instead. "Ask the paralegal about the
  // transcript" and "Get the paralegal to cancel the deposition" then both led
  // with "paralegal" and collapsed into one chip, losing a distinct offer the
  // model made (review finding, 2026-09-02).
  //
  // Fold accents the way significantTokens does (NFD, drop combining marks)
  // BEFORE the ASCII filter. Without that step a precomposed accented letter
  // is not in [a-z0-9], so it acted as a word separator and truncated the verb
  // at the accent: "Pídele…" and "Público…" both became "p" and matched, which
  // reintroduced the very collapse this guard prevents — for Spanish, French
  // and Portuguese chips instead of short English ones (review finding,
  // 2026-09-02, on the same-day fix above).
  const leadWord = (text: string): string => {
    const [word] = normalize(text)
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s]/g, ' ')
      .trim()
      .split(/\s+/);
    return word || '';
  };
  if (leadWord(a) !== leadWord(b)) return false;
  let shared = 0;
  ta.forEach((t) => { if (tb.has(t)) shared += 1; });
  const smaller = Math.min(ta.size, tb.size);
  return shared >= 2 && shared / smaller >= 0.6;
}

/**
 * Fold an incoming set into the list already on screen.
 *
 * - UPGRADED chips lead, in the order the model returned them.
 * - Anything already shown keeps its relative order behind them.
 * - A near-duplicate collapses to the upgraded wording, in the upgraded slot
 *   (the fast stage never saw the answer, so its phrasing is the weaker one).
 * - Nothing already shown is dropped except by falling off the length cap.
 */
export function mergeSuggestionLists(
  existing: ReadonlyArray<MergedSuggestion>,
  incoming: ReadonlyArray<string>,
  incomingStage: SuggestionStage,
): MergedSuggestion[] {
  const clean = incoming
    .map((t) => (t || '').trim())
    .filter((t) => t.length > 0)
    .map((text) => ({ text, stage: incomingStage }));
  if (clean.length === 0) return [...existing];

  const deduped: MergedSuggestion[] = [];
  for (const chip of clean) {
    if (!deduped.some((k) => chipsAreNearDuplicates(k.text, chip.text))) deduped.push(chip);
  }

  // Upgraded chips take the lead; a fast set arriving after an upgraded one
  // (out-of-order delivery) falls in behind what is already shown.
  const leading = incomingStage === 'upgraded' ? deduped : [];
  const trailing = incomingStage === 'upgraded' ? [] : deduped;

  const out: MergedSuggestion[] = [...leading];
  const push = (chip: MergedSuggestion) => {
    if (out.some((k) => chipsAreNearDuplicates(k.text, chip.text))) return;
    out.push(chip);
  };
  for (const chip of existing) push(chip);
  for (const chip of trailing) push(chip);
  return out.slice(0, MAX_MERGED_SUGGESTIONS);
}
